/**
 * useEtfPasteJob.js — Phase 36 (UI simplification).
 *
 * Self-contained hook for the ISIN-paste flow on the user side:
 *
 *   1. submitPaste(text)
 *        → POST /api/bulk-isin-paste
 *        → returns { ok, jobId } or { ok: false, error }
 *
 *   2. The hook then polls GET /api/bulk-isin-upload/{jobId} every 1.5 s
 *      until status === 'completed' (matches the admin's BulkIsinUploadDialog
 *      poll cadence) and tracks `processed/total` for live UI feedback.
 *
 *   3. On completion, it classifies the result into three buckets and
 *      invokes the `onComplete` callback with:
 *        {
 *          full:    [{ isin, etf_name, ticker, holdings_count }, …],
 *          partial: [{ isin, etf_name, reason }, …],
 *          failed:  [{ isin, reason }, …],
 *        }
 *
 *   4. While the job runs, the hook exposes the list of in-flight ISINs
 *      (`pendingIsins`) so the parent can render placeholder rows in the
 *      selection summary (Q4 inline-progress UX).
 *
 * Notes:
 *   * Only one job at a time. submitPaste during an active job is rejected
 *     with `{ ok: false, error: 'busy' }`.
 *   * The hook does NOT mutate `selectedEtfs` itself — the parent decides
 *     whether to auto-add `full` ETFs based on its own deduplication rules.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

import { formatApiError } from "../components/admin/shared";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 1500;

// Match the strict ISIN shape used by the backend (2 letters + 9 alnum + digit).
const ISIN_RE = /^[A-Z]{2}[A-Z0-9]{9}\d$/;


/** Extract candidate ISINs from a free-form blob, dedup + uppercase. */
function extractIsinsLocal(text) {
  if (!text) return [];
  const seen = new Set();
  const out = [];
  for (const raw of String(text).split(/[\s,;]+/)) {
    const candidate = raw.trim().toUpperCase();
    if (ISIN_RE.test(candidate) && !seen.has(candidate)) {
      seen.add(candidate);
      out.push(candidate);
    }
  }
  return out;
}


export function useEtfPasteJob({ onComplete } = {}) {
  const [job, setJob]               = useState(null);   // { jobId, total, processed, status }
  const [pendingIsins, setPending]  = useState([]);     // ISINs currently being imported
  const pollRef                     = useRef(null);

  // Stop any in-flight poller on unmount.
  useEffect(() => () => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // ----- Poll loop (recursive setTimeout, not setInterval, so we never
  // stack up multiple in-flight requests if the network is slow). -----
  const startPolling = useCallback((jobId) => {
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/bulk-isin-upload/${jobId}`);
        setJob((prev) => prev ? { ...prev, ...data } : null);

        if (data.status === "completed" || data.status === "failed") {
          if (typeof onComplete === "function") {
            const results = data.results || {};
            onComplete({
              full:    results.full    || [],
              partial: results.partial || [],
              failed:  results.failed  || [],
            });
          }
          setPending([]);
          setJob(null);
          pollRef.current = null;
          return;
        }
        pollRef.current = setTimeout(tick, POLL_INTERVAL_MS);
      } catch (err) {
        // Network blip — back off and retry once. After two consecutive
        // failures, give up to avoid an infinite poll storm.
        pollRef.current = setTimeout(tick, POLL_INTERVAL_MS * 2);
      }
    };
    pollRef.current = setTimeout(tick, POLL_INTERVAL_MS);
  }, [onComplete]);

  // ----- Public API -----
  const submitPaste = useCallback(async (text) => {
    if (job) {
      return { ok: false, error: "busy" };
    }
    const isins = extractIsinsLocal(text);
    if (isins.length === 0) {
      return { ok: false, error: "no-isin" };
    }
    try {
      const { data } = await axios.post(`${API}/bulk-isin-paste`, { text });
      setJob({
        jobId: data.job_id,
        filename: data.filename,
        total: data.total ?? isins.length,
        processed: 0,
        status: "running",
      });
      setPending(isins);
      startPolling(data.job_id);
      return { ok: true, jobId: data.job_id, isins };
    } catch (err) {
      return {
        ok: false,
        error: formatApiError(err, "Importazione ISIN fallita"),
      };
    }
  }, [job, startPolling]);

  const isBusy = !!job;

  return {
    job,
    pendingIsins,
    isBusy,
    submitPaste,
  };
}

// Pure helper exported for unit testing.
export { extractIsinsLocal };
